import type { MyEstimateGroup, MyEstimateRequestStatus } from '@massilia/queries'

import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import { CalendarClock, Clock, type LucideIcon } from 'lucide-react'

/**
 * Couleur des statuts de demande. Les libellés, eux, viennent des contrats :
 * une seule source, partagée avec la page publique du devis.
 *
 * `TARIFFS_LATER` est le seul badge plein — il ne décrit pas l'avancement d'une
 * demande mais un empêchement extérieur, et se confondrait sinon avec
 * « En cours » (DEVIS-CLIENT-PPT). L'icône double le signal, pour ne pas le
 * faire porter par la seule couleur.
 */
interface RequestBadgeTone {
  appearance: 'soft' | 'solid'
  tone: 'info' | 'neutral' | 'success' | 'warning'
}

export const REQUEST_BADGE_TONES = {
  ACTIVE: { appearance: 'soft', tone: 'info' },
  CLOSED: { appearance: 'soft', tone: 'neutral' },
  PROCESSING: { appearance: 'soft', tone: 'info' },
  // `soft` et non `solid` : c'est un avancement du dossier, pas un empêchement.
  RESERVATION_REQUESTED: { appearance: 'soft', tone: 'warning' },
  TARIFFS_LATER: { appearance: 'solid', tone: 'warning' },
} satisfies Record<MyEstimateRequestStatus, RequestBadgeTone>

/**
 * Les deux statuts que la couleur seule ne suffit pas à distinguer portent une
 * icône. Écrit une fois : la liste, le panneau et la page publique affichaient
 * chacun leur propre condition, et une seule des trois a suivi l'ajout de
 * « Réservation en attente ».
 */
interface RequestBadgeIcons {
  ACTIVE?: LucideIcon
  CLOSED?: LucideIcon
  PROCESSING?: LucideIcon
  RESERVATION_REQUESTED?: LucideIcon
  TARIFFS_LATER?: LucideIcon
}

export const REQUEST_BADGE_ICONS: RequestBadgeIcons = {
  RESERVATION_REQUESTED: Clock,
  TARIFFS_LATER: CalendarClock,
}

/**
 * Ce qu'une demande annonce d'elle-même dans la liste, en une ligne.
 *
 * Jamais une formule vague quand une date existe : une demande rangée annonce
 * le jour où ses tarifs paraissent, pas « bientôt » (principe 2 de PRODUCT.md).
 */
/** Ce qu'une demande annonce d'elle-même : sa ligne de résumé et ce qui attend le client. */
export interface RequestSummary {
  actionableCount: number
  summary: string
}

export function summarizeRequest(group: MyEstimateGroup): RequestSummary {
  const actionableCount = group.responses.filter((response) => response.canAct).length

  if (group.status === 'TARIFFS_LATER') {
    return {
      actionableCount,
      summary: group.tariffsAvailableAt
        ? `Tarifs attendus à partir du ${format(new Date(group.tariffsAvailableAt), 'd MMMM yyyy', { locale: fr })}`
        : 'En attente de la publication des tarifs',
    }
  }

  if (group.status === 'RESERVATION_REQUESTED') {
    const requestedAt = group.responses.find(
      (response) => response.status === 'RESERVATION_REQUESTED'
    )?.reservationRequestedAt

    return {
      actionableCount,
      summary: requestedAt
        ? `Réservation demandée le ${format(new Date(requestedAt), 'd MMMM yyyy', { locale: fr })}`
        : 'Réservation demandée',
    }
  }

  const responseCount = group.responses.length
  if (responseCount === 0) return { actionableCount, summary: 'Aucune proposition pour le moment' }

  return { actionableCount, summary: `${responseCount} proposition${responseCount > 1 ? 's' : ''}` }
}
